import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CloseIcon, CartIcon } from './Icons';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import Footer from './Footer';
import BookingDrawer from './BookingDrawer';
import Header from './Header';
import { triggerStockLimitAlert } from '../utils/firebaseService';

// Jaipur Heritage Palace Silhouette Accent (Matched to Fort Silhouette in Photo)
const PalaceSilhouette = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden z-0">
    <img
      src="/hawa_mahal_silhouette.jpg"
      alt="Hawa Mahal Palace Silhouette Jaipur"
      className="w-full h-full object-cover opacity-[0.045] mix-blend-multiply"
      loading="lazy"
      decoding="async"
    />
  </div>
);

export default function Homepage({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  favorites,
  setFavorites,
  cartItems,
  setCartItems,
  bookingStep,
  setBookingStep,
  bookingForm,
  setBookingForm,
  homeContent,
  products = [],
  collectionsData = {},
  setSelectedProduct,
  setSelectedCollectionId,
  currentUser,
  setCurrentUser,
}) {
  const videoRef = useRef(null);

  // Force autoplay on mount and when video is ready
  useEffect(() => {
    // Ken Burns animation plays automatically via CSS
    // videoRef is now attached to the primary banner image for pause-on-tap
  }, []);

  const [bannerPaused, setBannerPaused] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [undoItem, setUndoItem] = useState(null);
  const undoTimerRef = useRef(null);

  const handleVideoTap = () => {
    const el = videoRef.current;
    if (el) {
      if (el.tagName === 'VIDEO') {
        if (el.paused) {
          el.play();
          setBannerPaused(false);
        } else {
          el.pause();
          setBannerPaused(true);
        }
      } else {
        const next = !bannerPaused;
        setBannerPaused(next);
        el.style.animationPlayState = next ? 'paused' : 'running';
      }
    }
  };

  // Mobile New Arrivals Carousel States & Hooks
  const [activeNewArrival, setActiveNewArrival] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveNewArrival((prev) => (prev + 1) % 3);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const arrivalsTouchStart = useRef(0);
  const arrivalsTouchEnd = useRef(0);

  const handleTouchStart = useCallback((e) => {
    arrivalsTouchStart.current = e.targetTouches[0].clientX;
    arrivalsTouchEnd.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e) => {
    arrivalsTouchEnd.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const delta = arrivalsTouchStart.current - arrivalsTouchEnd.current;
    if (Math.abs(delta) > 50) {
      if (delta > 0) {
        setActiveNewArrival((prev) => (prev + 1) % 3);
      } else {
        setActiveNewArrival((prev) => (prev - 1 + 3) % 3);
      }
    }
  }, []);

  // Collections state — declared early so touch handlers can reference it
  const [currentCollection, setCurrentCollection] = useState(0);

  // Convert collectionsData to an array for the slider
  const collectionsList = Object.entries(collectionsData).map(([id, coll]) => ({
    id,
    title: coll.title,
    text: coll.description,
    image: coll.banner
  })).filter(c => c.title && c.title !== 'None');

  // If no collections are loaded yet, provide a fallback to prevent crash
  if (collectionsList.length === 0) {
    collectionsList.push({
      id: 1,
      title: "Loading...",
      text: "Loading collections...",
      image: ""
    });
  }

  // Get dynamic New Arrivals
  const newArrivalProducts = React.useMemo(() => {
    let newProds = (products || []).filter(p => p.isNewArrival);
    if (newProds.length < 3) {
      const others = (products || []).filter(p => !p.isNewArrival);
      newProds = [...newProds, ...others];
    }
    return newProds.slice(0, 3);
  }, [products]);

  // Auto transition collections slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentCollection((prev) => (prev + 1) % 6);
    }, 8500);
    return () => clearInterval(timer);
  }, []);

  // Touch handlers for Collections manual swipe
  const collectionTouchStart = useRef(0);
  const collectionTouchEnd = useRef(0);

  const handleCollectionTouchStart = useCallback((e) => {
    collectionTouchStart.current = e.targetTouches[0].clientX;
    collectionTouchEnd.current = e.targetTouches[0].clientX;
  }, []);

  const handleCollectionTouchMove = useCallback((e) => {
    collectionTouchEnd.current = e.targetTouches[0].clientX;
  }, []);

  const handleCollectionTouchEnd = useCallback(() => {
    const delta = collectionTouchStart.current - collectionTouchEnd.current;
    if (Math.abs(delta) > 50) {
      const len = collectionsList.length || 1;
      if (delta > 0) {
        setCurrentCollection((prev) => (prev + 1) % len);
      } else {
        setCurrentCollection((prev) => (prev - 1 + len) % len);
      }
    }
  }, [collectionsList.length]);


  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      const item = prev.find(i => String(i.id) === String(id));
      if (!item) return prev;
      if (item.qty + delta <= 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItem(item);
        undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
        return prev.filter(i => String(i.id) !== String(id));
      }
      const liveProd = (products || []).find(p => String(p.id) === String(id));
      const itemStock = (liveProd && typeof liveProd.stock === 'number')
        ? liveProd.stock
        : ((typeof item.stock === 'number') ? item.stock : 10);
      if (delta > 0 && item.qty + delta > itemStock) {
        triggerStockLimitAlert(itemStock);
        return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: itemStock } : i);
      }
      return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: i.qty + delta } : i);
    });
  };

  const removeItem = (id) => {
    const item = cartItems.find(i => i.id === id);
    if (item) {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    }
    setCartItems(prev => prev.filter(i => i.id !== id));
  };

  const handleUndo = () => {
    if (undoItem) {
      setCartItems(prev => [...prev, { ...undoItem, qty: 1 }]);
      setUndoItem(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    }
  };

  const getSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  };



  return (
    <div className="bg-noise min-h-screen relative font-sans text-[#584738] flex flex-col antialiased">
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* 2. HERO IMAGE SECTION */}
      <section className="relative w-full h-[62vh] min-h-[320px] portrait:h-[62vh] portrait:max-h-[750px] landscape:h-[68vh] lg:landscape:h-[82vh] max-h-[880px] overflow-hidden border-b border-hok-charcoal/5">
        <picture className="block w-full h-full">
          {homeContent?.mobileHeroImage ? (
            <>
              {/* All iPads & tablets in portrait orientation (including iPad Pro 13" at 1032px) */}
              <source
                media="(max-width: 1366px) and (orientation: portrait)"
                srcSet={homeContent.mobileHeroImage}
              />
              {/* Mobile phones */}
              <source
                media="(max-width: 768px)"
                srcSet={homeContent.mobileHeroImage}
              />
            </>
          ) : null}
          <img
            src={homeContent?.heroImage || "/hero_silver_jewelry_2x.jpg"}
            onError={e => { e.target.onerror = null; e.target.src = "/hero_silver_jewelry_2x.jpg"; }}
            alt="House of Karnnikaraa Heritage Bridal Silver Jewelry"
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
      </section>

      {/* 3. "OUR STORY" SECTION */}
      <section id="our-story" className="relative px-4 md:px-8 py-10 md:py-12 overflow-hidden z-10 bg-[#F1EADA]">
        <PalaceSilhouette />
        <div className="relative z-10 w-full text-center flex flex-col items-center">
          <h2 className="text-[22.8px] md:text-[34.2px] font-serif text-center font-normal tracking-wider md:tracking-widest uppercase text-[#584738] mb-3 leading-[1.1]">
            Our Story
          </h2>

          <div className="w-16 h-[0.75px] bg-[#584738]/20 mb-6"></div>

          {/* Paragraph styled in elegant Louis George Cafe Light */}
          <p className="font-sans text-[16px] md:text-[18px] leading-[1.8] text-hok-charcoal/80 text-center font-light max-w-4xl">
            Founded in 2017 by our artistic director & founder <span className="whitespace-nowrap">Mr. Ayush Atolia</span>, House of Karnnikaraa is an embodiment of Jaipur’s cultural spirit, artisanal heritage, and enduring legacy of silver. Rooted in the city’s centuries-old craft traditions, the house draws inspiration from royal ateliers, storied bazaars, frescoed havelis, and heirlooms passed through generations. Every creation is shaped through a confluence of artistry, emotion, and time-honoured craftsmanship—where silver becomes more than adornment; it becomes a keeper of memory, culture, and the timeless soul of Jaipur.
          </p>

          <span className="font-sans italic text-xl text-[#584738]/80 mt-8 tracking-widest block font-light select-none">
            — Est. 2017, Jaipur —
          </span>
        </div>
      </section>

      {/* 4. "DISCOVER OUR COLLECTIONS" SECTION (Serif styled) */}
      <section className="px-6 pt-6 pb-10 md:pt-8 md:pb-12 bg-[#584738]/[0.015] border-y border-[#584738]/5">
        <div className="max-w-5xl mx-auto flex flex-col items-center">
          <h2 className="text-2xl md:text-4xl font-serif text-center font-normal -mt-1 mb-6 tracking-wider text-[#584738] leading-[1.1]">
            Discover our collections
          </h2>

          {/* Landscape Featured Frame (Manually Swipable on mobile/touch screens) */}
          <div
            className="w-full relative aspect-[1.91/1] md:aspect-[2.4/1] rounded-xs overflow-hidden border border-[#584738]/10 shadow-xs group cursor-grab active:cursor-grabbing select-none cursor-pointer"
            onTouchStart={handleCollectionTouchStart}
            onTouchMove={handleCollectionTouchMove}
            onTouchEnd={handleCollectionTouchEnd}
            onClick={() => {
              if (collectionsList[currentCollection]?.id) {
                setSelectedCollectionId(collectionsList[currentCollection].id);
                setView('collections');
              }
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent z-10 pointer-events-none" />
            <img
              src={collectionsList[currentCollection].image}
              alt={collectionsList[currentCollection].title}
              className="w-full h-full object-cover transform scale-100 group-hover:scale-102 transition-transform duration-[6000ms] ease-out"
              loading="lazy"
              decoding="async"
            />
            {/* Overlay Title */}
            <div className="absolute bottom-6 left-6 md:left-10 z-20 text-left">
              <p className="text-[10px] md:text-[11px] tracking-widest text-white/70 uppercase mb-1.5 font-sans font-medium">Featured Collection</p>
              <h3 className="text-lg md:text-2xl font-serif text-white font-normal tracking-wide">
                {collectionsList[currentCollection].title}
              </h3>
            </div>
          </div>

          {/* Slide Indicator Dots */}
          <div className="flex gap-4 mt-6 mb-4">
            {collectionsList.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentCollection(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-500 focus:outline-none ${currentCollection === index
                  ? 'bg-[#584738] scale-120'
                  : 'bg-[#CEC1A8] hover:bg-[#584738]/40'
                  }`}
                aria-label={`Go to Collection ${index + 1}`}
              />
            ))}
          </div>

          {/* Description Text in Serif font */}
          <p className="font-sans text-[13.5px] md:text-[14.5px] leading-[1.85] text-center text-hok-charcoal/85 max-w-2xl px-2 font-light tracking-wide">
            {collectionsList[currentCollection].text}
          </p>
        </div>
      </section>

      {/* 5. "NEW ARRIVALS" SECTION (Clean Grid on Desktop, Auto/Manual Touch Carousel on Mobile) */}
      <section className="px-6 pt-6 pb-10 md:pt-8 md:pb-12">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-serif font-normal -mt-1 mb-6 tracking-wider text-[#584738] leading-[1.1]">
            New Arrivals
          </h2>

          {/* A. MOBILE VIEW: Auto-scrolling & Manual Touch Swipable Carousel */}
          <div className="block md:hidden max-w-[320px] sm:max-w-[360px] mx-auto select-none">
            <div
              className="relative w-full aspect-[4/5] rounded-xs overflow-hidden border border-hok-charcoal/5 shadow-xs"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Sliding wrapper */}
              <div
                className="absolute inset-0 flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${activeNewArrival * 100}%)` }}
              >
                {newArrivalProducts.map((prod, idx) => (
                  <div 
                    key={prod.id || idx}
                    onClick={() => {
                      setSelectedProduct(prod);
                      setView('product');
                    }}
                    className="w-full h-full flex-shrink-0 cursor-pointer"
                  >
                    <img src={prod.images?.[0] || ""} alt={prod.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </div>
                ))}
              </div>
            </div>

            {/* Sliding Indicator Dots for Mobile Manual Scroll */}
            <div className="flex gap-3 justify-center mt-5">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => setActiveNewArrival(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 focus:outline-none ${activeNewArrival === index ? 'bg-[#584738] scale-125' : 'bg-[#CEC1A8]/50 hover:bg-[#584738]/40'
                    }`}
                  aria-label={`Go to Arrival ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* B. DESKTOP VIEW: Pristine 3-Column Grid (Exactly Matched to Mockup) */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {newArrivalProducts.map((prod, idx) => (
              <div key={prod.id || idx} className="w-full flex flex-col items-center">
                <div 
                  onClick={() => {
                    setSelectedProduct(prod);
                    setView('product');
                  }}
                  className="relative w-full aspect-[4/5] rounded-xs overflow-hidden border border-hok-charcoal/5 shadow-xs cursor-pointer hover:shadow-md transition-shadow duration-300 group"
                >
                  <img src={prod.images?.[0] || ""} alt={prod.name} className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-500" loading="lazy" decoding="async" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-serif text-sm truncate">{prod.name}</p>
                    <p className="text-white/80 font-sans text-xs">Rs. {(prod.price || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Explore More Button Section */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => {
                if (window.setHokCategory) {
                  window.setHokCategory('New arrival');
                }
                setView('marketplace');
              }}
              className="border border-[#584738]/20 hover:border-[#584738] px-8 py-3 text-[13px] md:text-[14px] font-sans font-light text-[#584738] tracking-wider transition-all duration-500 ease-in-out focus:outline-none bg-transparent hover:bg-[#584738] hover:text-white relative z-10"
            >
              Explore Now &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* 6. CALLOUT BANNER — Ken Burns Animated Jewelry Slideshow (with Admin Video Override) */}
      <section
        className="relative w-full aspect-[16/10] sm:aspect-auto sm:h-[45vh] md:h-[55vh] min-h-[240px] max-h-[500px] flex items-center justify-center overflow-hidden border-y border-[#584738]/5 cursor-pointer bg-black"
        onClick={handleVideoTap}
      >
        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10 z-10 pointer-events-none" />

        {homeContent?.calloutVideo && homeContent.calloutVideo !== '/jewelry_video.mp4' ? (
          /* Admin Custom Video */
          <video
            ref={videoRef}
            src={homeContent.calloutVideo}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          /* Original Ken Burns Slideshow */
          <>
            {/* Layer 1: Primary Ken Burns image (banner jewelry) */}
            <img
              ref={videoRef}
              src="/banner_silver_jewelry.jpg"
              alt="House of Karnnikaraa Jewelry"
              className="animate-ken-burns absolute inset-0 w-full h-full object-cover object-center"
              style={{ transformOrigin: 'center center' }}
              loading="lazy"
              decoding="async"
            />

            {/* Layer 2: Secondary image cross-fading in */}
            <img
              src="/new_arrival_2.jpg"
              alt="Karnnikaraa Silver Jewelry Woman"
              className="animate-ken-burns-alt absolute inset-0 w-full h-full object-cover object-[center_15%]"
              style={{ transformOrigin: '60% 40%' }}
              loading="lazy"
              decoding="async"
            />
          </>
        )}

        {/* Centered Overlaid Content */}
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-20 text-center select-none w-full max-w-xs">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setView('marketplace');
            }}
            className="border border-white/30 hover:border-white px-8 py-3 text-[11px] md:text-[12px] tracking-[0.25em] font-sans font-light text-white transition-all duration-500 ease-in-out focus:outline-none uppercase bg-transparent hover:bg-white hover:text-[#584738] cursor-pointer"
          >
            DISCOVER MORE
          </button>
        </div>
      </section>

      {/* 7. "OUR STORIES" / APPOINTMENT SECTION */}
      <section id="boutiques" className="relative px-6 py-10 md:py-12 overflow-hidden z-10 bg-[#F1EADA]">
        <PalaceSilhouette />
        <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
          <span className="text-[11px] md:text-[12px] tracking-[0.3em] text-[#584738]/70 font-sans font-semibold uppercase mb-2">
            OUR STORE
          </span>
          <h2 className="text-2xl md:text-4.5xl font-serif leading-tight font-normal text-serif-italic text-[#584738] tracking-wide mb-4">
            In The Heart Of Pink City - Jaipur
          </h2>

          <div className="w-16 h-[0.75px] bg-[#584738]/20 mb-6"></div>

          {/* Paragraph styled in elegant serif, matching photo */}
          <div className="font-sans text-[14.5px] md:text-[15.5px] leading-[1.95] text-hok-charcoal/90 text-center font-light tracking-wider max-w-2xl px-2 md:px-0 mb-6 flex flex-col gap-2">
            <p>
              In Jaipur, craftsmanship was never confined to ateliers – it was woven into the very fabric of the city. Rooted within its historic nine-grid plan, the “36 karkhanas” shaped Jaipur’s enduring artisanal identity, where silver making emerged as one of its most celebrated traditions through generations of karigars.
            </p>
            <p>
              Inspired by this living heritage, House of Karnnikaraa reimagine silver as a contemporary expression of Jaipur’s timeless craft tradition.
            </p>
          </div>

          {/* Book Appointment Action */}
          <button
            onClick={() => setIsBookingOpen(true)}
            className="border border-[#584738]/20 hover:border-[#584738] px-8 py-3 text-[11px] md:text-[12px] tracking-[0.25em] font-sans font-light text-[#584738] transition-all duration-500 ease-in-out focus:outline-none uppercase bg-transparent hover:bg-[#584738] hover:text-white"
          >
            BOOK AN APPOINTMENT
          </button>
        </div>
      </section>

      {/* 7.5. EXACT FIGMA TRUST SECTION */}
      <section id="craftsmanship" className="relative px-3 sm:px-8 md:px-2 lg:px-6 xl:px-14 py-10 md:py-12 border-y border-[#584738]/10 overflow-hidden z-10">
        <div className="max-w-7xl mx-auto flex flex-col items-center">
          {/* Grid Layout: 2 col on mobile (2/2 split), 4 cols on tablet & desktop with elegant dividers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-0 gap-x-0 w-full">
            
            {/* Pillar 1: Handcrafted Artistry */}
            <div className="relative flex flex-col items-center px-2 sm:px-4 md:px-1 lg:px-2 xl:px-4 py-4 md:py-2 text-center border-r border-b border-[#584738]/10 md:border-r-0 md:border-b-0 group">
              <div className="h-10 flex items-center justify-center text-[#584738] mb-2 group-hover:scale-105 transition-transform duration-300">
                <img src="/Icon.svg" className="h-10 w-10 object-contain opacity-85" alt="Handcrafted Artistry" />
              </div>
              <h3 className="font-serif text-[11px] sm:text-[14px] md:text-[10.5px] lg:text-[13px] xl:text-[18px] font-normal tracking-wide sm:tracking-wider mb-2 text-[#584738] whitespace-nowrap text-center">
                HANDCRAFTED ARTISTRY
              </h3>
              <p className="font-sans text-[10px] sm:text-[12px] md:text-[11px] lg:text-[12.5px] xl:text-[14px] tracking-normal sm:tracking-wide leading-[1.65] md:leading-[1.7] text-[#584738]/80 max-w-[190px] md:max-w-[175px] lg:max-w-[200px] xl:max-w-[220px] px-0 sm:px-1.5 md:px-0.5 xl:px-2 font-light">
                Time-honoured techniques & refined details shape every jewel.
              </p>
              <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-[0.75px] h-24 bg-[#584738]/15"></div>
            </div>

            {/* Pillar 2: Heritage Reimagined */}
            <div className="relative flex flex-col items-center px-2 sm:px-4 md:px-1 lg:px-2 xl:px-4 py-4 md:py-2 text-center border-b border-[#584738]/10 md:border-b-0 group">
              <div className="h-10 flex items-center justify-center text-[#584738] mb-2 group-hover:scale-105 transition-transform duration-300">
                <img src="/Icons-01.svg" className="h-10 w-10 object-contain opacity-85" alt="Heritage Reimagined" />
              </div>
              <h3 className="font-serif text-[11px] sm:text-[14px] md:text-[10.5px] lg:text-[13px] xl:text-[18px] font-normal tracking-wide sm:tracking-wider mb-2 text-[#584738] whitespace-nowrap text-center">
                HERITAGE REIMAGINED
              </h3>
              <p className="font-sans text-[10px] sm:text-[12px] md:text-[11px] lg:text-[12.5px] xl:text-[14px] tracking-normal sm:tracking-wide leading-[1.65] md:leading-[1.7] text-[#584738]/80 max-w-[190px] md:max-w-[175px] lg:max-w-[200px] xl:max-w-[220px] px-0 sm:px-1.5 md:px-0.5 xl:px-2 font-light">
                Rooted in Jaipur, each jewel carries enduring heritage.
              </p>
              <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-[0.75px] h-24 bg-[#584738]/15"></div>
            </div>

            {/* Pillar 3: Conscious Craft */}
            <div className="relative flex flex-col items-center px-2 sm:px-4 md:px-1 lg:px-2 xl:px-4 py-4 md:py-2 text-center border-r border-[#584738]/10 md:border-r-0 group">
              <div className="h-10 flex items-center justify-center text-[#584738] mb-2 group-hover:scale-105 transition-transform duration-300">
                <img src="/Icons-03.svg" className="h-10 w-10 object-contain opacity-85" alt="Conscious Craft" />
              </div>
              <h3 className="font-serif text-[11px] sm:text-[14px] md:text-[10.5px] lg:text-[13px] xl:text-[18px] font-normal tracking-wide sm:tracking-wider mb-2 text-[#584738] whitespace-nowrap text-center">
                CONSCIOUS CRAFT
              </h3>
              <p className="font-sans text-[10px] sm:text-[12px] md:text-[11px] lg:text-[12.5px] xl:text-[14px] tracking-normal sm:tracking-wide leading-[1.65] md:leading-[1.7] text-[#584738]/80 max-w-[190px] md:max-w-[175px] lg:max-w-[200px] xl:max-w-[220px] px-0 sm:px-1.5 md:px-0.5 xl:px-2 font-light">
                925 silver, carefully selected, reflects our material standards.
              </p>
              <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-[0.75px] h-24 bg-[#584738]/15"></div>
            </div>

            {/* Pillar 4: Made to Endure */}
            <div className="relative flex flex-col items-center px-2 sm:px-4 md:px-1 lg:px-2 xl:px-4 py-4 md:py-2 text-center group">
              <div className="h-10 flex items-center justify-center text-[#584738] mb-2 group-hover:scale-105 transition-transform duration-300">
                <img src="/Icons-02.svg" className="h-10 w-10 object-contain opacity-85" alt="Made to Endure" />
              </div>
              <h3 className="font-serif text-[11px] sm:text-[14px] md:text-[10.5px] lg:text-[13px] xl:text-[18px] font-normal tracking-wide sm:tracking-wider mb-2 text-[#584738] whitespace-nowrap text-center">
                MADE TO ENDURE
              </h3>
              <p className="font-sans text-[10px] sm:text-[12px] md:text-[11px] lg:text-[12.5px] xl:text-[14px] tracking-normal sm:tracking-wide leading-[1.65] md:leading-[1.7] text-[#584738]/80 max-w-[190px] md:max-w-[175px] lg:max-w-[200px] xl:max-w-[220px] px-0 sm:px-1.5 md:px-0.5 xl:px-2 font-light">
                Created with lasting quality, each jewel becomes cherished.
              </p>
            </div>

          </div>
        </div>
      </section>


      <Footer setView={setView} setIsBookingOpen={setIsBookingOpen} />

      {/* --- SIDE-OVERS & DRAWER PANELS --- */}

      {/* A. MOBILE MENU DRAWER */}
      <MenuDrawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} setView={setView} setIsBookingOpen={setIsBookingOpen} setSelectedCollectionId={setSelectedCollectionId} currentUser={currentUser} setCurrentUser={setCurrentUser} setIsAuthOpen={setIsAuthOpen} />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />
      {/* B. LUXURY CART SLIDE-OVER */}
<div
  className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${
    isCartOpen ? "opacity-100 pointer-events-auto" : "opacity-0"
  }`}
>
  {/* Backdrop */}
  <div
    onClick={() => setIsCartOpen(false)}
    className="absolute inset-0 bg-black/40 backdrop-blur-xs"
  />

  {/* Cart Drawer */}
  <div
    className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-noise shadow-2xl p-6 transition-transform duration-300 flex flex-col ${
      isCartOpen ? "translate-x-0" : "translate-x-full"
    }`}
  >
    <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
      <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
        Your Cart
        <span className="text-xs bg-[#584738]/10 text-[#584738] px-2.5 py-0.5 rounded-full font-sans font-bold">
          {cartItems.reduce((acc, it) => acc + it.qty, 0)}
        </span>
      </h3>

      <button
        onClick={() => setIsCartOpen(false)}
        className="text-[#584738] hover:text-[#584738] focus:outline-none"
        aria-label="Close Cart"
      >
        <CloseIcon />
      </button>
    </div>

    <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1 scrollbar-thin">
      {cartItems.length === 0 ? (
        <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
          <CartIcon />
          <p className="mt-3 text-sm tracking-wide font-medium">
            Your cart is empty.
          </p>

          <button
            onClick={() => setIsCartOpen(false)}
            className="text-[#584738] hover:underline text-xs tracking-widest font-semibold uppercase mt-2"
          >
            Start Shopping
          </button>
        </div>
      ) : (
        cartItems.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="flex gap-4 border-b border-[#584738]/5 pb-4 font-serif"
          >
            <img
              src={item.image}
              alt={item.name}
              className="w-16 h-20 object-cover border border-[#584738]/5"
            />

            <div className="flex-1 flex flex-col justify-between">
              <div>
                <h4 className="text-sm font-serif font-medium tracking-wide text-[#584738]/90 leading-tight">
                  {item.name}
                </h4>

                <p className="text-[12.5px] text-[#584738] font-medium mt-1">
                  ₹{item.price.toLocaleString("en-IN")} <span className="text-[9.5px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                </p>
              </div>

              <div className="flex items-center justify-between text-xs mt-2 font-sans">
                <div className="flex items-center border border-[#584738]/20 select-none">
                  <button
                    onClick={() => handleQtyChange(item.id, -1)}
                    className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none"
                  >
                    -
                  </button>

                  <span className="px-3 py-0.5 text-center font-medium bg-[#584738]/2.5">
                    {item.qty}
                  </span>

                  <button
                    onClick={() => handleQtyChange(item.id, 1)}
                    className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none"
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={() => removeItem(item.id)}
                  className="text-[#584738]/60 hover:text-red-500 font-semibold"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>

    {cartItems.length > 0 && (
      <div className="border-t border-[#584738]/10 pt-6 mt-6 flex flex-col gap-4 flex-shrink-0">
        <div className="flex items-center justify-between font-serif">
          <div className="flex flex-col text-left">
            <span className="text-md font-medium tracking-wide text-[#584738]/80">
              Subtotal
            </span>
            <span className="text-[10px] text-[#584738]/60 font-sans normal-case">
              (Inclusive of all taxes)
            </span>
          </div>

          <span className="text-lg font-bold text-[#584738]">
            ₹{getSubtotal().toLocaleString("en-IN")}
          </span>
        </div>

        <p className="text-[10px] text-[#584738]/60 tracking-wider text-center font-sans">
          Complimentary Express Shipping • (Inclusive of all taxes)
        </p>

        <button
          onClick={() => {
            setView('checkout');
            setIsCartOpen(false);
          }}
          className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors shadow-xs font-sans"
        >
          PROCEED TO CHECKOUT
        </button>
      </div>
    )}

    {/* ── UNDO TOAST ── */}
    {undoItem && (
      <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3 bg-[#584738] text-[#F5E6D8] px-4 py-3 rounded-xs animate-fade-in">
        <p className="text-[11.5px] font-sans tracking-wide truncate flex-1">
          <span className="opacity-60 mr-1">Removed:</span>{undoItem.name}
        </p>
        <button
          onClick={handleUndo}
          className="flex items-center gap-1.5 text-[11px] font-sans font-bold tracking-widest uppercase text-[#CEC1A8] hover:text-white transition-colors focus:outline-none flex-shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
          </svg>
          Undo
        </button>
      </div>
    )}
  </div>
</div>

      {/* C. BOOK SALON APPOINTMENT PANEL */}
      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => { setIsBookingOpen(false); setBookingStep(1); }}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
      />

    </div>
  );
}
